import {Component, OnDestroy, OnInit} from '@angular/core';
import {AsyncPipe, NgForOf, NgIf} from '@angular/common';
import {NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet} from '@angular/router';
import {MatListModule} from '@angular/material/list';
import {MatIconModule} from '@angular/material/icon';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatSelectModule} from '@angular/material/select';
import {FormsModule} from '@angular/forms';
import {Observable, Subscription} from 'rxjs';
import {filter, map} from 'rxjs/operators';

import {AuthService} from '../services/auth.service';
import {EvaluationsService} from '../services/evaluations.service';
import {hasRole, isEvaluationAdmin, User} from '../model/user';

/**
 * Shell for the broadcast evaluation section.
 *
 * Admin and producer share the section but not the navigation: a producer has
 * no business seeing the season overview or the review queue, both of which
 * show every production company's numbers.
 */
@Component({
  selector: 'app-evaluation',
  templateUrl: './evaluation.component.html',
  styleUrls: ['./evaluation.component.scss'],
  standalone: true,
  imports: [
    RouterLink,
    RouterLinkActive,
    RouterOutlet,
    MatListModule,
    MatIconModule,
    MatFormFieldModule,
    MatSelectModule,
    FormsModule,
    AsyncPipe,
    NgForOf,
    NgIf,
  ]
})
export class EvaluationComponent implements OnInit, OnDestroy {

  user$: Observable<User>;
  isAdmin$: Observable<boolean>;
  seasons$: Observable<string[]>;
  /** The season on screen. Held plainly so the picker can bind both ways. */
  current = '';

  /** Admin-only screens a producer must never land on, not even by a stale link. */
  private readonly adminOnly = ['/evaluation', '/evaluation/season', '/evaluation/criteria',
    '/evaluation/review'];
  private subscriptions = new Subscription();

  constructor(
    private auth: AuthService,
    private evaluations: EvaluationsService,
    private router: Router,
  ) {
    this.user$ = this.auth.user$;
    this.isAdmin$ = this.user$.pipe(map(user => isEvaluationAdmin(user)));
    this.seasons$ = this.evaluations.seasons();
  }

  /**
   * Whether the season in view is the one being worked on now. An old season
   * stays reachable on purpose, so the sidebar has to say when you are in one
   * — otherwise "where have my broadcasts gone" is the next question.
   */
  get onLatestSeason(): boolean {
    const latest = this.evaluations.latestSeason;
    return !latest || !this.current || this.current === latest;
  }

  chooseSeason(season: string): void {
    this.evaluations.selectSeason(season);
  }

  ngOnInit(): void {
    this.subscriptions.add(
      this.evaluations.currentSeason().subscribe(season => this.current = season));
    // The section's default child is the season overview, which is the admin's
    // home. A producer arriving at /evaluation gets sent to their own list
    // instead of an empty table the rules would not let them read anyway.
    this.subscriptions.add(
      this.user$.subscribe(user => this.redirectProducer(user, this.router.url)));
    this.subscriptions.add(
      this.router.events.pipe(filter(event => event instanceof NavigationEnd))
        .subscribe((event: NavigationEnd) => this.redirectProducer(this.auth.user, event.urlAfterRedirects)));
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private redirectProducer(user: User, url: string): void {
    if (!isEvaluationAdmin(user) && hasRole(user, 'producer')
        && this.adminOnly.includes(url.split('?')[0])) {
      this.router.navigate(['/evaluation/my-games']);
    }
  }
}
